import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { sendChatMessage } from '@/services/api';
import { MessageSquare, Send, Bot, User, Loader2, AlertCircle } from 'lucide-react';

const TenantChat = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Add welcome message
    setMessages([{
      role: 'assistant',
      content: `Hello ${user?.name}! I'm your FortiHome AI assistant. I can help you with:\n\n• Understanding your rent payments and lease details\n• Information about society rules and regulations\n• General queries about your tenancy\n• Tips for maintaining good relations with your landlord\n\nHow can I assist you today?`
    }]);
  }, [user?.name]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await sendChatMessage(userMessage, sessionId);
      setMessages(prev => [...prev, { role: 'assistant', content: response.response }]);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to get response. Please try again.';
      if (errorMessage.includes('AI features are disabled')) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'AI features are currently not available. Please contact the administrator to enable AI features by configuring the GROQ_API_KEY.',
          isError: true
        }]);
      } else {
        toast.error(errorMessage);
        if (error.response?.status === 429) {
          setMessages(prev => [...prev, { role: 'assistant', content: errorMessage, isError: true }]);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedQuestions = [
    "What are the society rules?",
    "When is my rent due?",
    "How do I contact my landlord?",
    "What amenities are available?"
  ];

  return (
    <div className="space-y-6" data-testid="tenant-chat">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">AI Assistant</h1>
        <p className="text-slate-600 mt-1">Get help with your queries</p>
      </div>

      <Card className="h-[calc(100vh-250px)] flex flex-col">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="h-8 w-8 bg-secondary rounded-full flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            FortiHome Assistant
          </CardTitle>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex items-start gap-2 max-w-[80%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.role === 'user' ? 'bg-secondary' : message.isError ? 'bg-red-100' : 'bg-slate-100'
                }`}>
                  {message.role === 'user' ? (
                    <User className="h-5 w-5 text-white" />
                  ) : message.isError ? (
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  ) : (
                    <Bot className="h-5 w-5 text-secondary" />
                  )}
                </div>
                <div className={`p-3 rounded-2xl ${
                  message.role === 'user' 
                    ? 'bg-secondary text-white rounded-tr-sm' 
                    : message.isError 
                      ? 'bg-red-50 text-red-800 border border-red-200 rounded-tl-sm'
                      : 'bg-slate-100 text-slate-900 rounded-tl-sm'
                }`}>
                  <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                </div>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-2xl rounded-tl-sm">
                <Loader2 className="h-4 w-4 animate-spin text-secondary" />
                <span className="text-sm text-slate-600">Thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </CardContent>

        {/* Suggested Questions */}
        {messages.length <= 1 && (
          <div className="px-4 pb-2">
            <p className="text-xs text-slate-500 mb-2">Suggested questions:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((question, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setInput(question);
                    setTimeout(() => handleSend(), 100);
                  }}
                  className="text-xs"
                >
                  {question}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Input
              maxLength={1000}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={loading}
              className="flex-1"
              data-testid="chat-input"
            />
            <Button 
              onClick={handleSend} 
              disabled={loading || !input.trim()}
              data-testid="send-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TenantChat;
